import { twMerge } from 'tailwind-merge'
import { DateTime } from 'luxon'
import { Fragment, ReactNode, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'

import {
  useBusinessUnitQuery,
  useCostCodeAutoCompleteQuery,
  usePhaseCodeAutoCompleteQuery,
  useJobAutoCompleteQuery,
  useOrgMachineDetailsQuery,
  useOrganizationDetailsQuery,
  useSelectedDealerLocationsQuery,
  useSelectedLocationQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import Box from '@/gf/components/Box'
import Field from '@/gf/components/next/forms/Field'
import { SimpleTooltip } from '@/gf/components/next/Tooltip'
import useToggle from '@/gf/hooks/useToggle'
import Phone from '@/gf/modules/Phone'
import ImagesList from './ImagesList'
import PreviewEmailButton from './ReviewStep/PreviewEmailButton'
import UpdateLocationModal from './ReviewStep/UpdateLocationModal'
import UpdatePhoneModal from './ReviewStep/UpdatePhoneModal'
import StartOverButton from './StartOverButton'
import StickyBottomBar from './StickyBottomBar'
import { PartialRequestUpdate } from './types'
import PONumberModal from './ReviewStep/PONumberModal'
import Ghost from '@/gf/components/Ghost'
import BillingAddress, { BillingAddressGhost } from '@/gf/components/BillingAddress'

const ChangeDetails = ({
  onChangeClick,
  title,
  text,
}: {
  onChangeClick: () => void
  title?: React.ReactNode
  text: React.ReactNode
}) => (
  <div className="flex items-start">
    <div className="flex-grow text-sm leading-5">
      {title && <h5 className="mb-0.5">{title}</h5>}
      <div className="text-gray-600">{text}</div>
    </div>
    <Action.T type="button" onClick={onChangeClick} className="font-medium text-sm leading-5">
      Change
    </Action.T>
  </div>
)

const Details = ({
  title,
  children,
  className,
}: {
  title?: ReactNode
  children: ReactNode
  className?: string
}) => (
  <div className={twMerge('text-gray-600 text-sm leading-5', className)}>
    {title && <h5 className="text-gray-900 mb-0.5">{title}</h5>}
    {children}
  </div>
)

const ReviewStep = ({
  reset,
  request,
  updateRequest,
  onBackClicked,
  onCreateRequestClicked,
  submitButtonText,
  submitInProgress,
  updateLocationEnabled = true,
}: {
  reset?: () => void
  onBackClicked: () => void
  onCreateRequestClicked: (poNumber?: string) => void
  submitButtonText?: string
  submitInProgress: boolean
  updateLocationEnabled?: boolean
} & PartialRequestUpdate) => {
  const client = useGqlClient()
  const [locationModalOpen, locationModalToggle] = useToggle()
  const [poNumberModalOpen, poNumberModalToggle] = useToggle()
  const [updatePhoneModalOpen, updatePhoneModalToggle] = useToggle()
  const {
    orgId,
    user,
    features,
    organization: {
      id: organizationId,
      generatePurchaseOrderNumber,
      requestsJobNumbersEnabled,
      costCodesEnabled,
    },
  } = useSession()
  const phaseCodesEnabled = features.phaseCodesEnabled

  const {
    data: orgData,
    loading: _loadingOrg,
    refetch: refetchOrg,
  } = useOrganizationDetailsQuery({
    client,
    variables: { id: organizationId },
  })

  const organization = orgData?.org ?? null

  const { data: locationData, loading: _loadingLocation } = useSelectedLocationQuery({
    client,
    variables: { orgId, id: request.locationId as string },
    skip: !request.locationId,
  })

  const location = locationData?.shippingLocationsSearch.shippingLocations?.[0] ?? null

  const { data: machineData, loading: _loadingMachine } = useOrgMachineDetailsQuery({
    client,
    variables: { id: request.machineOrgId as string },
    skip: !request.machineOrgId,
  })

  const machine = machineData?.orgMachine ?? null

  const { data: businessUnitData } = useBusinessUnitQuery({
    client,
    variables: { branchId: request.billingCompanyId ?? null },
  })
  const company = businessUnitData?.company
  const billingAddress =
    businessUnitData?.billingAddress ?? location?.address ?? request.shippingAddress

  const { data: selectedVendorsData } = useSelectedDealerLocationsQuery({
    client,
    variables: {
      orgId,
      nearPoint: null,
      ids: request.vendors.map((v) => v.vendorId),
      tutorial: request.tutorial,
    },
    skip: request.vendors.length === 0,
  })

  const vendors = useMemo(
    () =>
      selectedVendorsData
        ? request.vendors.map((reqVendor) => {
            const vendor = selectedVendorsData.dealerLocations.entries.find(
              (dl) => (dl.vendor?.id as string) === reqVendor.vendorId
            )

            return {
              ...vendor,
              contacts: vendor?.contacts.filter((c) => reqVendor.contactIds.includes(c.id)) ?? [],
              accountNumber: reqVendor.accountNumber,
            }
          })
        : [],
    [selectedVendorsData, request.vendors]
  )

  const allSelectedJobIds =
    request.parts?.flatMap((p) => p.jobQuantities.map((jq) => jq.jobId)) ?? []
  const { data: allJobsData } = useJobAutoCompleteQuery({
    client,
    variables: { search: null, ids: allSelectedJobIds },
  })

  const allJobCodes: Record<string, string> = useMemo(() => {
    return (
      allJobsData?.jobAutocomplete.reduce((acc, job) => ({ ...acc, [job.id]: job.code }), {}) ?? {}
    )
  }, [allJobsData])

  const allSelectedCostCodeIds = (request.parts?.map((p) => p.costCodeId ?? '') ?? []).filter(
    (costCodeId) => !!costCodeId
  )
  const { data: allCostCodesData } = useCostCodeAutoCompleteQuery({
    client,
    variables: { search: null, ids: allSelectedCostCodeIds },
  })
  const allCostCodes: Record<string, string> = useMemo(() => {
    return (
      allCostCodesData?.costCodesAutocomplete.reduce(
        (acc, costCode) => ({ ...acc, [costCode.id]: costCode.code }),
        {}
      ) ?? {}
    )
  }, [allCostCodesData])

  const allSelectedPhaseCodeIds = (request.parts?.map((p) => p.phaseCodeId ?? '') ?? []).filter(
    (phaseCodeId) => !!phaseCodeId
  )
  const { data: allPhaseCodesData } = usePhaseCodeAutoCompleteQuery({
    client,
    variables: { search: null, ids: allSelectedPhaseCodeIds },
  })
  const allPhaseCodes: Record<string, string> = useMemo(() => {
    return (
      allPhaseCodesData?.phaseCodesAutocomplete.reduce(
        (acc, phaseCode) => ({ ...acc, [phaseCode.id]: phaseCode.code }),
        {}
      ) ?? {}
    )
  }, [allPhaseCodesData])

  const onSendRequest = () => {
    const needsPoNumber = request.quickOrder && !generatePurchaseOrderNumber

    if (needsPoNumber) {
      poNumberModalToggle.on()
    } else {
      onCreateRequestClicked()
    }
  }

  const shippingAddress = request.shippingAddress ?? location?.address

  const incompleteLocationAddress =
    !location?.address?.lineOne ||
    !location?.address?.city ||
    !location?.address?.state ||
    !location?.address?.postalCode ||
    !location?.address?.country

  if (!organization || !(location || request.shippingAddress) || !shippingAddress) {
    return null
  }

  // locationId is null on some flows and undefined on others (e.g. the approve
  // wizard seeds it with ?. off a free-floating integration address) — both
  // mean "no saved location backs this address".
  const hasShippingAddressOnly = request.shippingAddress && request.locationId == null
  const locationIdRequired = updateLocationEnabled && !hasShippingAddressOnly

  const submitText =
    submitButtonText ??
    (request.quickOrder
      ? 'Send Order'
      : `Send ${request.vendors.length > 1 ? 'All Requests' : 'Request'}`)

  return (
    <>
      <PONumberModal
        open={poNumberModalOpen}
        onClose={poNumberModalToggle.off}
        onPoNumberConfirmed={(poNumber) => onCreateRequestClicked(poNumber)}
      />

      <UpdateLocationModal
        open={locationModalOpen}
        onClose={locationModalToggle.off}
        locationId={request.locationId as string}
        onChange={(locationId) => updateRequest({ locationId })}
      />

      <UpdatePhoneModal
        open={updatePhoneModalOpen}
        onClose={updatePhoneModalToggle.off}
        onSuccess={refetchOrg}
      />

      <Box className="p-8">
        <div className="max-w-md space-y-4">
          <p className="leading-6">Your request details</p>
          <Field label="Request from">
            <div className="flex flex-row items-center gap-x-2">
              {company ? (
                <>
                  {company?.logoUrl && (
                    <img
                      src={company.logoUrl}
                      alt={company.name}
                      className="h-8 w-auto rounded-sm object-contain"
                    />
                  )}
                  <p className="text-sm leading-5 mb-0.5">{company?.name}</p>
                </>
              ) : (
                <>
                  <Ghost className="w-8 h-8" />
                  <Ghost className="w-32 h-6" />
                </>
              )}
            </div>
            <Details>
              Requested by: {user.displayName}
              <br />
              Email: {user.email}
              <br />
              {user.phoneNumber ? (
                <>Phone: {Phone.format(user.phoneNumber)}</>
              ) : (
                <button
                  type="button"
                  className="underline underline-offset-2 text-red-500 text-bold"
                  onClick={() => updatePhoneModalToggle.on()}
                >
                  Add Phone Number
                </button>
              )}
            </Details>
          </Field>

          <hr />

          <Field label="Bill to">
            {company ? (
              <Details>
                {billingAddress ? (
                  <BillingAddress companyName={company.name} address={billingAddress} />
                ) : (
                  <p className="text-gray-400 italic">(none)</p>
                )}
              </Details>
            ) : (
              <div>
                <BillingAddressGhost />
              </div>
            )}
          </Field>

          <hr />

          <Field label="Drop-off Location">
            {locationIdRequired ? (
              <ChangeDetails
                onChangeClick={locationModalToggle.on}
                text={
                  incompleteLocationAddress ? (
                    <div className="space-y-2">
                      <p className="text-gray-600 text-sm leading-5">{location?.name}</p>
                      <Link
                        to={`/locations/${location?.id}?edit=true`}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors w-fit"
                      >
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 text-yellow-600" />
                        Incomplete address - click to update
                      </Link>
                    </div>
                  ) : (
                    <>
                      {location?.name && (
                        <>
                          <span className="text-gray-900">{location?.name}</span>
                          <br />
                        </>
                      )}
                      {location?.address?.lineOne}
                      <br />
                      {location?.address?.city}, {location?.address?.state}{' '}
                      {location?.address?.postalCode}
                      <br />
                      {location?.address?.country}
                      <br />
                    </>
                  )
                }
              />
            ) : request?.shippingAddress ? (
              <Details>
                {request?.shippingAddress?.lineOne}
                <br />
                {request?.shippingAddress?.city}, {request?.shippingAddress?.state}{' '}
                {request?.shippingAddress?.postalCode}
                <br />
                {request?.shippingAddress?.country}
                <br />
                <p className="text-gray-400 italic mt-1">Not one of your saved locations</p>
              </Details>
            ) : (
              <Details>
                {location?.name && (
                  <>
                    <span className="text-gray-900">{location?.name}</span>
                    <br />
                  </>
                )}
                {location?.address?.lineOne}
                <br />
                {location?.address?.city}, {location?.address?.state}{' '}
                {location?.address?.postalCode}
                <br />
                {location?.address?.country}
                <br />
              </Details>
            )}
          </Field>

          {request.vendors.length > 0 && (
            <>
              <hr />

              <Field label="Vendors">
                <ChangeDetails
                  onChangeClick={onBackClicked}
                  text={
                    <ul className="flex flex-col gap-y-2">
                      {vendors.map((v) => (
                        <li key={v.id}>
                          <span className="block text-gray-900">
                            {v.name} -{' '}
                            <span className="italix text-gray-600">Account #{v.accountNumber}</span>
                          </span>

                          <ul className="flex gap-x-4">
                            {v.contacts.map((c) => (
                              <li key={c.id} className="text-xs text-gray-600 inline-block">
                                {c.name}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  }
                />
              </Field>
            </>
          )}

          <hr />

          <Field label="Request status">
            <Details>
              Requested date: {DateTime.now().toFormat('D')}
              <br />
              Needed by: {request.urgency?.neededByDate.toFormat('D')}
              <br />
              Machine down: {request.urgency?.machineDown ? 'Yes' : 'No'}
            </Details>
          </Field>

          <hr />

          <Field label="Fitment for machine">
            <Details>
              Unit number: {machine?.name} <br />
              Serial number: {machine?.serialNumber}
              <br />
              Make: {machine?.machine.make ?? 'Unknown'}
              <br />
              Model: {machine?.machine.model ?? 'Unknown'}
              <br />
              Year: {machine?.machine.year ?? 'Unknown'}
            </Details>
          </Field>

          <hr />

          <Field label="Parts requested">
            <div className="space-y-3">
              {request.parts?.map((part) => (
                <Fragment key={part.partNumber ?? part.description}>
                  <Details key={part.partNumber}>
                    Part number: {part.partNumber || 'N/A'} <br />
                    {part.description && (
                      <>
                        Description: {part.description}
                        <br />
                      </>
                    )}
                    Quantity: {(part.rfqQuantity ?? part.quantity).toFixed(0)}
                    {costCodesEnabled && part.costCodeId && (
                      <>
                        <br />
                        Cost code: {allCostCodes[part.costCodeId]}
                      </>
                    )}
                    {phaseCodesEnabled && part.phaseCodeId && (
                      <>
                        <br />
                        Phase code: {allPhaseCodes[part.phaseCodeId]}
                      </>
                    )}
                    {requestsJobNumbersEnabled &&
                      part.jobQuantities &&
                      part.jobQuantities.length > 0 && (
                        <>
                          <br />
                          Job numbers:{' '}
                          {part.jobQuantities.map((jq) => allJobCodes[jq.jobId] ?? '').join(', ')}
                        </>
                      )}
                  </Details>
                  {part.pictures.length > 0 && <ImagesList imageUrls={part.pictures} />}
                </Fragment>
              ))}
            </div>
          </Field>

          {request.comments && (
            <>
              <hr />

              <Field label="Additional details">
                <Details>{request.comments}</Details>
              </Field>
            </>
          )}
        </div>
      </Box>
      <StickyBottomBar>
        {reset && <StartOverButton reset={reset} />}

        {request.vendors.length > 0 && company && billingAddress !== undefined && (
          <PreviewEmailButton
            request={request}
            orgMachine={machine}
            company={company}
            billingAddress={billingAddress}
            shippingAddress={shippingAddress}
          />
        )}

        <div className="flex-grow" />
        <Action.S onClick={onBackClicked}>Previous</Action.S>
        {user.phoneNumber ? (
          <Action.P color="blue" onClick={onSendRequest} performing={submitInProgress}>
            {submitText}
          </Action.P>
        ) : (
          <SimpleTooltip
            placement="top-start"
            text="You must add a phone number to submit"
            className="lg:max-w-56"
          >
            <Action.P
              color="blue"
              onClick={onSendRequest}
              performing={submitInProgress}
              disabled={!user.phoneNumber}
            >
              {submitText}
            </Action.P>
          </SimpleTooltip>
        )}
      </StickyBottomBar>
    </>
  )
}

export default ReviewStep
